// packages import
import { FC, useContext, useEffect, useState } from 'react'
import { Button, CircularProgress, DialogActions, Grid } from '@mui/material'

// others
import { CANCEL } from '../../../constants'
import { ActionType } from '../../../context/AppContextReducer'
import { yupResolver } from '@hookform/resolvers/yup'
import { AppContext } from '../../../context'
import { addNewJudgeValidationSchema } from '../../../validationSchema'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import {
  ExternalUserRoleRole,
  ExternalUserRolesPayload,
  ExternalUserStatus,
  useCreateExternalUserMutation,
  useFetchAllExternalUserRolesQuery
} from '../../../../generated'
import { AddNewClientProps, CreateNewJudge, ErrorException } from '../../../interfaceTypes'

// component import
import { Alert } from '../../common/Alert'
import { CustomModal } from '../../common/CustomModal'
import { InputFormFields } from '../../externalUsers/InputFormFields'

export const AddNewJudge: FC<AddNewClientProps> = ({ refetch, clientType, setNewExternalUser }) => {
  const { isOpen: isEdit, dispatch: appDispatch } = useContext(AppContext)
  const [allExternalUserRoles, setAllExternalUserRoles] = useState<ExternalUserRolesPayload['externalUserRoles']>()
  const judgeUserRole = allExternalUserRoles?.find(role => role?.role === ExternalUserRoleRole.Judge)
  const { id: judgeUserRoleId } = judgeUserRole || {}

  // eslint-disable-next-line
  const { loading } = useFetchAllExternalUserRolesQuery({
    onCompleted(data) {
      if (data) {
        const {
          fetchAllExternalUserRoles: { response, externalUserRoles }
        } = data
        if (response) {
          const { status } = response

          if (status === 200 && externalUserRoles) {
            setAllExternalUserRoles(externalUserRoles)
          }
        }
      }
    }
  })

  const methods = useForm<CreateNewJudge>({
    mode: 'all',
    resolver: yupResolver(addNewJudgeValidationSchema),

    defaultValues: {
      firstName: '',
      lastName: '',
      status: ExternalUserStatus.Active,
      externalUserRoleId: judgeUserRoleId || '',
      phoneNumber: '',
      email: '',
      phoneNumberExtension: ''
    }
  })

  const [createExternalUserMutation, { loading: creteExternalUserLoader }] = useCreateExternalUserMutation({
    onError(error) {
      const { graphQLErrors } = error
      if (graphQLErrors) {
        graphQLErrors.map(
          // eslint-disable-next-line
          ({ extensions }) => {
            if (extensions) {
              const { exception } = extensions
              if (exception) {
                const { response } = exception as ErrorException
                const { response: errorResponse } = response || {}
                if (errorResponse) {
                  const { error } = errorResponse

                  if (error === 'Case number already exists') {
                    Alert.error('Matter number already exists')
                  } else {
                    Alert.error(error)
                  }
                }
              }
            }
          }
        )
      }
    },

    onCompleted(data) {
      if (data) {
        const {
          createExternalUser: { response, externalUser }
        } = data || {}

        if (response) {
          const { status } = response

          if (status === 200 && externalUser) {
            Alert.success('External User Created Successfully')
            refetch && refetch()
            handleClose()
            setNewExternalUser && setNewExternalUser(externalUser)
            setValue('externalUserRoleId', judgeUserRoleId || '')
            if (isEdit) {
              appDispatch({ type: ActionType.SET_IS_EDIT, isEdit: false })
            }
          }
        }
      }
    }
  })

  const { reset, handleSubmit, setValue } = methods
  const handleClose = () => {
    if (isEdit) {
      appDispatch({ type: ActionType.SET_IS_EDIT, isEdit: false })
      appDispatch({ type: ActionType.SET_IS_OPEN, isOpen: false })
    }
    reset()
  }

  const onSubmit: SubmitHandler<CreateNewJudge> = async (data: CreateNewJudge) => {
    const { firstName, lastName, phoneNumber, email, externalUserRoleId, phoneNumberExtension } = data

    await createExternalUserMutation({
      variables: {
        createExternalUserInput: {
          firstName: firstName?.trim() || undefined,
          lastName: lastName?.trim() || undefined,
          status: ExternalUserStatus.Active,
          externalUserRoleId: externalUserRoleId || '',
          phoneNumber: phoneNumber ?? undefined,
          email,
          phoneNumberExtension
        }
      }
    })
  }

  useEffect(() => {
    judgeUserRoleId && setValue('externalUserRoleId', judgeUserRoleId)
    // eslint-disable-next-line
  }, [judgeUserRole, setValue, clientType])

  return (
    <CustomModal title="Add New Judge" isOpen={!!isEdit} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} rowSpacing={2}>
            <InputFormFields isJudge showExtension />
          </Grid>

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose} variant="outlined" aria-label={CANCEL}>
              {CANCEL}
            </Button>

            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={creteExternalUserLoader}
              aria-label="add new judge"
              endIcon={creteExternalUserLoader && <CircularProgress size={20} color="inherit" />}
            >
              Add
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}
